import { getAssistantSurface } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

/** The pages `show_notebook` opens, as the model names them. */
const VIEWS = ['list', 'notebook', 'run'] as const
type ScriptsView = (typeof VIEWS)[number]

/** Matches the server's cap on a notebook or run id in the page context. */
const MAX_ID = 64

/** A page reads its notebook before it reports it; past this the page is shown without a claim. */
const SHOW_WAIT_MS = 4_000
const POLL_MS = 100

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

interface ShowRequest {
  view: ScriptsView
  notebookId?: string
  runId?: string
}

/** A trimmed id, '' when absent, or null when it is present but not a usable id. */
function readId(raw: unknown): string | null {
  if (raw === undefined) return ''
  if (typeof raw !== 'string') return null

  const id = raw.trim()

  return id.length > MAX_ID ? null : id
}

function readRequest(input: unknown): ShowRequest | { error: string } {
  const raw = (input ?? {}) as { view?: unknown; notebook_id?: unknown; run_id?: unknown }
  const rawView = raw.view === undefined ? 'list' : raw.view

  if (!VIEWS.includes(rawView as ScriptsView)) return { error: "Invalid view: expected 'list', 'notebook' or 'run'." }

  const view = rawView as ScriptsView

  const notebookId = readId(raw.notebook_id)
  if (notebookId === null) return { error: 'Invalid notebook_id.' }

  const runId = readId(raw.run_id)
  if (runId === null) return { error: 'Invalid run_id.' }

  if (view === 'list') return { view }
  if (!notebookId) return { error: `The '${view}' view needs a notebook_id.` }
  if (view === 'notebook') return { view, notebookId }
  if (!runId) return { error: "The 'run' view needs a run_id." }

  return { view, notebookId, runId }
}

/** `run` opens the script page with `?run=`, which `ScriptPage` reads to select that run's rendered output. */
function pathOf(req: ShowRequest): string {
  if (req.view === 'list') return '/scripts'

  const page = `/scripts/${encodeURIComponent(req.notebookId!)}`

  return req.view === 'run' ? `${page}?run=${encodeURIComponent(req.runId!)}` : page
}

/** True once the scripts page on screen reports the requested view, notebook and run. */
function showing(req: ShowRequest): boolean {
  const handle = getAssistantSurface()
  if (handle?.kind !== 'scripts') return false

  const ctx = handle.readContext().scripts
  if (req.view === 'list') return ctx.view === 'list'
  if (ctx.view !== 'notebook' || ctx.notebook?.id !== req.notebookId) return false

  return req.view !== 'run' || ctx.notebook?.lastRun?.id === req.runId
}

async function awaitShowing(req: ShowRequest): Promise<boolean> {
  const deadline = Date.now() + SHOW_WAIT_MS

  for (;;) {
    if (showing(req)) return true
    if (Date.now() >= deadline) return false
    await delay(POLL_MS)
  }
}

/**
 * `show_notebook`. `refusal` is the route manifest's answer for `/scripts` for this caller,
 * resolved by `executeClientTool` (which owns the resolver), or null when allowed.
 */
export async function executeShowNotebook(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusal: string | null,
): Promise<unknown> {
  const req = readRequest(input)
  if ('error' in req) return req

  if (refusal) return { refused: refusal }

  const target = pathOf(req)
  const [targetPath, targetQuery = ''] = target.split('?')
  // Selecting a run of the script already open stays on the same page, so nothing is left behind.
  const onTargetPage = deps.currentRoute() === targetPath
  const discard = (input as { discardUnsaved?: unknown } | null)?.discardUnsaved === true

  if (!onTargetPage && !discard) {
    const unsaved = deps.hasUnsavedWork()
    if (unsaved) return { blocked_unsaved_work: unsaved }
  }

  // Showing what is already shown must not add a Back entry.
  const currentQuery = (deps.currentSearch?.() ?? '').replace(/^\?/, '')
  if (onTargetPage && currentQuery === targetQuery) deps.navigate(target, { replace: true })
  else deps.navigate(target)

  const notebookId = req.notebookId ? { notebook_id: req.notebookId } : {}

  return (await awaitShowing(req))
    ? { shown: true, view: req.view, ...notebookId }
    : { shown: true, note: 'The Scripts page did not report this view in time; it is open but may still be loading.' }
}
